import { useMemo } from 'react';

import RecommendedProductCard from '../RecommendedProductCard';
import { useSegmentation } from './SegmentationRenderer';

function productIdentity(product, index) {
  const id = product?.product_id ?? product?.id ?? product?.sku;
  if (id != null && String(id).trim()) return String(id).trim();
  const name = String(product?.product_name || product?.name || '').trim().toLowerCase();
  return name || `product-${index}`;
}

export function normalizeProductList(products) {
  if (!products) return [];

  if (Array.isArray(products)) {
    const seen = new Set();
    return products.filter((product, idx) => {
      const key = productIdentity(product, idx);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  let morning = [];
  let evening = [];

  if (Array.isArray(products.morning) || Array.isArray(products.evening)) {
    morning = products.morning || [];
    evening = products.evening || [];
  } else if (products.recommended_products) {
    const nested = products.recommended_products;
    if (Array.isArray(nested)) {
      return normalizeProductList(nested);
    }
    morning = nested.morning || [];
    evening = nested.evening || [];
  }

  const byId = new Map();

  const upsert = (product, routine, index) => {
    if (!product || typeof product !== 'object') return;
    const key = productIdentity(product, index);
    const existing = byId.get(key);
    if (existing) {
      if (routine && !existing.routines.includes(routine)) {
        existing.routines.push(routine);
      }
      return;
    }
    byId.set(key, {
      product,
      routines: routine ? [routine] : [],
    });
  };

  morning.forEach((product, idx) => upsert(product, 'Morning', idx));
  evening.forEach((product, idx) => upsert(product, 'Evening', idx));

  return Array.from(byId.values()).map(({ product, routines }) => ({
    ...product,
    _routines: routines,
  }));
}

const ProductCards = ({
  products,
  cartItems = {},
  onAddToCart,
  onViewProduct,
  storeUrl = '',
}) => {
  const segmentation = useSegmentation();
  const list = useMemo(() => {
    const normalized = normalizeProductList(products);
    if (segmentation.productRecommendationSource === 'products_page') {
      return normalized;
    }
    const limit = segmentation.productSuggestLimit;
    if (typeof limit === 'number' && limit >= 1) {
      return normalized.slice(0, limit);
    }
    return normalized;
  }, [products, segmentation.productSuggestLimit, segmentation.productRecommendationSource]);

  if (!segmentation.loaded || !segmentation.productSuggestion || list.length === 0) {
    return null;
  }

  return (
    <div className="skin-routine-section-block skin-routine-products-block">
      <div className="skin-routine-section-heading-with-info">
        <h2 className="skin-routine-section-heading">Recommended Products</h2>
      </div>
      <div className="skin-routine-product-recommendations-content product-recommendations-content">
        <div className="recommended-live-product-grid">
          {list.map((product, idx) => {
            const productId = product.product_id || product.id || product.sku
              || `prod_${(product.product_name || product.name || '').replace(/\s+/g, '_')}_${idx}`;
            const cartQuantity = cartItems[productId]?.quantity || 0;

            return (
              <RecommendedProductCard
                key={productId}
                product={product}
                productId={productId}
                cartQuantity={cartQuantity}
                showAddToCart={segmentation.addToCart}
                showProductLink={segmentation.productLink}
                onAddToCart={onAddToCart}
                onViewProduct={onViewProduct}
                storeUrl={storeUrl}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProductCards;
